import { loggedInUserRelationshipDefault } from '../../../shared/logged-in-user-default.js'
import { matchesDisplayCondition, formattingToStyle as displayFormattingToStyle } from '../../../shared/display-rules.js'
import { matchingSubmissionActions } from '../../../shared/submission-rules.js'

/**
 * Client-side rule evaluator for reactive form formatting.
 *
 * This is a lightweight mirror of the backend rule-engine-module.js.
 * It handles 'self' and 'field' source types only — 'related' conditions
 * are skipped (they require DB access and are evaluated server-side).
 *
 * Usage:
 *   import { evaluateFormFormatting } from './client-rule-evaluator'
 *   const formatting = evaluateFormFormatting(fieldComponents, formValues)
 *   // formatting = { fieldUniqueTitle: { bold: true, color: '#ff0000' }, ... }
 */

function castValue (value, dataType) {

  if (dataType === 'number') {
    const num = Number(value)
    return isNaN(num) ? value : num
  }
  if (dataType === 'checkbox') {
    if (value === 'true' || value === true) return true
    if (value === 'false' || value === false) return false
    return !!value
  }
  return value
}

function evaluateCondition (condition, formValues, columnUniqueTitle) {

  // Skip related conditions — they can't be evaluated client-side
  if (condition.source === 'related') return false

  let testValue

  switch (condition.source) {

    case 'self':
      testValue = formValues[columnUniqueTitle]
      break

    case 'field':
      testValue = formValues[condition.field]
      break

    default:
      testValue = formValues[columnUniqueTitle]
      break
  }

  const conditionValue = castValue(condition.value, condition.dataType)

  if (condition.dataType === 'number') {
    testValue = castValue(testValue, 'number')
  } else if (condition.dataType === 'checkbox') {
    testValue = castValue(testValue, 'checkbox')
  }

  return matchesDisplayCondition(testValue, condition.operator, conditionValue, condition.dataType)
}

function evaluateConditions (conditions, matchMode, formValues, columnUniqueTitle) {

  if (!conditions || conditions.length === 0) return true

  if (matchMode === 'any') {
    return conditions.some(c => evaluateCondition(c, formValues, columnUniqueTitle))
  }

  return conditions.every(c => evaluateCondition(c, formValues, columnUniqueTitle))
}

function extractFormattingActions (actions) {

  if (!actions || actions.length === 0) return null

  const formatting = {}
  let hasProperties = false

  for (const action of actions) {

    if (action.type === 'formatting' && action.property) {
      formatting[action.property] = action.value
      hasProperties = true
    }
  }

  return hasProperties ? formatting : null
}

function evaluateColumnRules (ruleConfig, formValues, columnUniqueTitle) {

  if (!ruleConfig || !ruleConfig.rules || ruleConfig.rules.length === 0) return null

  for (const rule of ruleConfig.rules) {

    if (rule.type === 'else') {
      return extractFormattingActions(rule.actions)
    }

    const matched = evaluateConditions(
      rule.conditions,
      rule.conditionMatchMode || 'all',
      formValues,
      columnUniqueTitle
    )

    if (matched) {
      return extractFormattingActions(rule.actions)
    }
  }

  return null
}

/**
 * Walk the nested form component tree and collect all leaf components that have formattingRules.
 * Returns an array of { uniqueTitle, formattingRules }.
 */
function collectFieldComponents (components) {

  if (!components) return []

  const result = []

  for (const comp of components) {

    if (comp.formattingRules && comp.formattingRules.rules && comp.formattingRules.rules.length > 0) {
      result.push({ uniqueTitle: comp.uniqueTitle, formattingRules: comp.formattingRules })
    }

    if (comp.components) {
      result.push(...collectFieldComponents(comp.components))
    }
  }

  return result
}

/**
 * Main entry: evaluate formatting rules for all fields given current form values.
 * @param {Array} formComponents - The full nested form component tree (from page config)
 * @param {Object} formValues - Current form values keyed by field uniqueTitle
 * @returns {Object} Map of { fieldUniqueTitle: { bold, color, bgColor, ... } }
 */
export function evaluateFormFormatting (formComponents, formValues) {

  const fieldsWithRules = collectFieldComponents(formComponents)

  if (fieldsWithRules.length === 0) return {}

  const formatting = {}

  for (const field of fieldsWithRules) {

    const result = evaluateColumnRules(field.formattingRules, formValues, field.uniqueTitle)

    if (result) {
      formatting[field.uniqueTitle] = result
    }
  }

  return formatting
}

/**
 * Evaluate default value rules for a form on load.
 * Returns an object with:
 *   defaults: { fieldName: value, ... }  — values to set on form fields
 *   readonly: { fieldName: true, ... }   — fields to make read-only
 *   relatedLookups: [{ field, relationshipColumn, relatedField }, ...] — related values to fetch server-side
 *
 * The if/elseif/else chain is evaluated; only the first matching rule's actions apply.
 *
 * @param {Object} defaultValueRules - { rules: [...] } config stored on the form component
 * @param {Object} formValues - Current form values keyed by field uniqueTitle
 * @returns {Object} { defaults, readonly, relatedLookups }
 */
export function evaluateDefaultValueRules (defaultValueRules, formValues) {

  const result = { defaults: {}, readonly: {}, relatedLookups: [], relationshipDefaults: [] }

  if (!defaultValueRules || !defaultValueRules.rules || defaultValueRules.rules.length === 0) return result

  for (const rule of defaultValueRules.rules) {

    let matched = false

    if (rule.type === 'else') {
      matched = true
    } else {
      const conditions = (rule.conditions || []).filter(c => c.source !== 'none')
      const hasAlwaysCondition = (rule.conditions || []).some(c => c.source === 'none')

      if (hasAlwaysCondition && conditions.length === 0) {
        matched = true
      } else if (conditions.length === 0) {
        matched = true
      } else {
        matched = evaluateConditions(
          conditions,
          rule.conditionMatchMode || 'all',
          formValues,
          null
        )
      }
    }

    if (matched) {

      for (const action of (rule.actions || [])) {

        if (!action.field) continue

        if (action.setValue) {
          const loggedInUser = loggedInUserRelationshipDefault(action)

          if (loggedInUser) {
            result.relationshipDefaults.push(loggedInUser)
          } else if (action.relSelectedRecords && action.relSelectedRecords.length > 0) {
            // Relationship target field: link these records
            result.relationshipDefaults.push({
              field: action.field,
              records: action.relSelectedRecords
            })
          } else if (action.valueSource === 'related' && action.relationshipColumn && action.relatedField) {
            result.relatedLookups.push({
              field: action.field,
              relationshipColumn: action.relationshipColumn,
              relatedField: action.relatedField
            })
          } else if (action.valueSource === 'static') {
            result.defaults[action.field] = action.staticValue ?? ''
          }
        }

        if (action.setReadonly) {
          result.readonly[action.field] = true
        }
      }

      return result
    }
  }

  return result
}

/**
 * Evaluate submission rules for a form after submit.
 * Returns the actions array from the first matching rule, or null if no rules match.
 * @param {Object} submissionRules - { rules: [...] } config stored on the form component
 * @param {Object} formValues - Current form values keyed by field uniqueTitle
 * @returns {Array|null} Array of submission action objects, or null
 */
export function evaluateSubmissionRules (submissionRules, formValues, context) {

  return matchingSubmissionActions(submissionRules, formValues, context)
}

/**
 * Convert a formatting object to an inline style object suitable for Vue :style binding.
 */
export function formattingToStyle (fmt) {

  return displayFormattingToStyle(fmt)
}
